import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Modal component that shows a success confirmation for a scheduled appointment and optionally displays structured order/vehicle details.
 * Purpose: Presents a confirmation screen with a title, description, optional detailed order information (package, customer, vehicle), and a primary action button. It handles closing via a close button or backdrop click and formats vehicle information for display.
 * Key methods:
 *   - vehicleDisplay (getter): Builds a user-friendly vehicle string (e.g., "2022 Toyota Camry") while handling missing/any values and filtering out invalid year values like "0".
 *   - onClose(): Emits the close event to request closing the modal.
 *   - onPrimaryAction(): Emits the primaryAction event when the primary call-to-action is triggered.
 *   - onBackdropClick(event: MouseEvent): Closes the modal when the backdrop (outside modal content) is clicked.
 * Lifecycle hooks: This component does not implement Angular lifecycle hooks (e.g., OnInit/OnDestroy) in the current implementation.
 * @component
 * @selector app-success-modal
 * @example
 *   <app-success-modal
 *     [isVisible]="true"
 *     [title]="'Appointment Confirmed!'"
 *     [description]="'Your appointment was scheduled.'"
 *     [buttonText]="'Go to Appointment Details'"
 *     [packageName]="'Full Detail'"
 *     [customerName]="'Jane Doe'"
 *     [vehicleMake]="'Toyota'"
 *     [vehicleModel]="'Camry'"
 *     [vehicleYear]="'2022'"
 *     (close)="handleClose()"
 *     (primaryAction)="handlePrimary()">
 *   </app-success-modal>
 * @input {boolean} isVisible - Controls visibility of the modal. True shows the modal.
 * @input {string} title - Title text displayed at the top of the modal. Defaults to 'Appointment Confirmed!'.
 * @input {string} description - Descriptive text explaining the success state. Defaults to a scheduling confirmation message.
 * @input {string} buttonText - Label for the primary action button. Defaults to 'Go to Appointment Details'.
 * @input {string} packageName - Name of the selected service/package; used to show structured order details when available.
 * @input {string} customerName - Customer's name to display in the order details section.
 * @input {string|undefined} vehicleMake - Vehicle make (e.g., 'Toyota'). If missing/any, vehicle display falls back to 'N/A'.
 * @input {string|undefined} vehicleModel - Vehicle model (e.g., 'Camry'). Omitted if empty or any.
 * @input {string|undefined} vehicleYear - Vehicle year (e.g., '2022'). Ignored when empty, '0', or any.
 * @output {EventEmitter<void>} close - Emitted when the modal should be closed (close button, backdrop click).
 * @output {EventEmitter<void>} primaryAction - Emitted when the primary action button is clicked (navigates to appointment details or other handler).
 */
@Component({
  selector: 'app-success-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './success-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./success-modal.component.scss'],
})
export class SuccessModalComponent {
  @Input() isVisible = false;
  @Input() title = 'Appointment Confirmed!';
  @Input() description =
    'The car detailing appointment for the customer is scheduled successfully.';
  @Input() buttonText = 'Go to Appointment Details';

  // Order details inputs for structured display
  @Input() packageName = '';
  @Input() customerName = '';
  @Input() vehicleMake: string | undefined = '';
  @Input() vehicleModel: string | undefined = '';
  @Input() vehicleYear: string | undefined = '';

  @Output() close = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();

  // Check if order details are available to show structured layout
  // Show new UI only if package, customer name, make, model, and year are all available
  // get hasOrderDetails(): boolean {
  //   // return true;
  //   return !!(this.packageName && this.customerName && this.vehicleMake && this.vehicleModel );
  // }

  // Format vehicle display string (e.g., "2022 Toyota Camry")
  /**
   * Return a human-readable vehicle description built from the component's year, make, and model.
   * @example
   * this.vehicleDisplay()
   * '2020 Toyota Camry'
   * @returns {{string}} Formatted vehicle string (e.g. "2020 Make Model") or "N/A" if make is missing, empty, 'any', 'N/A', or year is invalid/zero.
   **/
  get vehicleDisplay(): string {
    // Helper function to check if value is empty or any
    const isEmptyOrUnknown = (value: string | undefined): boolean => {
      if (!value) return true;
      const trimmed = value.trim();
      return trimmed === '' || trimmed.toLowerCase() === 'any' || trimmed === 'N/A';
    };

    const parts: string[] = [];

    if (
      this.vehicleYear &&
      this.vehicleYear.trim() !== '' &&
      this.vehicleYear.trim() !== '0' &&
      !isEmptyOrUnknown(this.vehicleYear)
    ) {
      parts.push(this.vehicleYear);
    }

    if (this.vehicleMake && !isEmptyOrUnknown(this.vehicleMake)) {
      parts.push(this.vehicleMake);
    }

    if (this.vehicleModel && !isEmptyOrUnknown(this.vehicleModel)) {
      parts.push(this.vehicleModel);
    }

    if (parts.length > 0) {
      return parts.join(' ');
    }

    return 'N/A';
  }

  onClose(): void {
    this.close.emit();
  }

  onPrimaryAction(): void {
    this.primaryAction.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
